namespace Portico.Services {

	import pme = Portico.Models.Enterprise;
	import pmg = Portico.Models.Gateway;
	import psga = apigateway.client;
	import psgm = apigateway.models;
    import Inject = Portico.Decorators.Inject;
    import Service = Portico.Decorators.Service;

    export interface IPrescriberService {
		create(addresses?: number, emailAddresses?: number, externalIds?: number, phoneNumbers?: number, prescriberLicenses?: number): angular.IPromise<pme.Prescriber>;
		get(prescriberId: number): angular.IPromise<pme.Prescriber>;
		populate(account: pme.Account): angular.IPromise<boolean>;
		save(prescriber: pme.Prescriber): angular.IPromise<pme.Prescriber>;
		search(prescriber: pme.Prescriber): angular.IPromise<pme.Prescriber[]>;
    }

    @Service('app', 'prescriberService')
	@Inject('$q', 'PrescriberV3Api')
    export class PrescriberService implements IPrescriberService {
        constructor(
            private $q: angular.IQService,
			private prescriberV3Api: psga.PrescriberV3Api
        ) { }

		create(addresses: number = 0, emailAddresses: number = 0, externalIds: number = 0, phoneNumbers: number = 0, prescriberLicenses: number = 0): angular.IPromise<pme.Prescriber> {
			const prescriber = new pme.Prescriber();

			for (let i = 0; i < addresses; i++) {
				prescriber.addAddress();
			}

			for (let i = 0; i < emailAddresses; i++) {
				prescriber.addEmailAddress();
			}

			for (let i = 0; i < externalIds; i++) {
				prescriber.addExternalId();
			}

			for (let i = 0; i < phoneNumbers; i++) {
				prescriber.addPhoneNumber();
			}

			for (let i = 0; i < prescriberLicenses; i++) {
				prescriber.addPrescriberLicense();
			}

			return this.$q.when(prescriber);
		}

		get(prescriberId: number): angular.IPromise<pme.Prescriber> {
			// TODO: wrap in an edge call
			return this.$q.when(this.prescriberV3Api.prescriberGetByTenantId(prescriberId).then((response) => {
				return new pme.Prescriber(response.data.data);
			}));
		}

		/** @deprecated */
		length(account: pme.Account): number {
			return account.prescriberRelationships.length;
		}

		populate(account: pme.Account): angular.IPromise<boolean> {
			const promises: angular.IPromise<void>[] = [];

			for (const relationship of account.prescriberRelationships) {
				if (!relationship.prescriber) {
					const promise = this.get(relationship.prescriberId).then((response) => {
						relationship.prescriber = response;
					});

					promises.push(promise);
				}
			}

			return this.$q.all(promises).then(() => {
				return true;
			});
		}

		save(prescriber: pme.Prescriber): angular.IPromise<pme.Prescriber> {
			const request: psgm.PrescriberUpsearchModel = {
				prescriberId: _.toNumber(prescriber.id),
				firstName: _.get(prescriber, 'firstName', ''),
				middleName: _.get(prescriber, 'middleName', ''),
				lastName: _.get(prescriber, 'lastName', ''),
				suffix: _.get(prescriber, 'suffix', ''),
				nickname: _.get(prescriber, 'nickName', ''),
				npi: _.get(prescriber, 'npi', ''),
				taxId: _.get(prescriber, 'taxId', ''),
				externalIds: _.map(prescriber.externalIds, (v) => {
					return {
						id: v.id,
						externalIdType: _.toNumber(v.type),
						externalId: _.get(v, 'value', '')
					};
				}),
				addresses: _.map(prescriber.addresses, (v) => {
					return {
						addressId: v.id,
						addressType: _.toNumber(v.type),
						addressOne: _.get(v, 'address1', ''),
						addressTwo: _.get(v, 'address2', ''),
						city: _.get(v, 'city', ''),
						state: _.get(v, 'state', ''),
						zip: _.get(v, 'zip', ''),
						country: _.get(v, 'country', '')
					};
				}),
				phoneNumbers: _.map(prescriber.phoneNumbers, (v) => {
					return {
						phoneId: v.id,
						phoneType: _.toNumber(v.type),
						phonePurpose: _.toNumber(v.purpose),
						phoneNumber: _.get(v, 'number', ''),
						extension: _.get(v, 'extension', ''),
						contactDatePreferenceType: _.toNumber(v.contactDatePreferenceType),
						bestDaysToCall: _.get(v, 'bestDaysToCall', ''),
						contactTimePreferenceType: _.toNumber(v.contactTimePreferenceType),
						bestTimeToCall: _.get(v, 'bestTimeToCall', ''),
						language: _.get(v, 'language', ''),
						timeZone: _.get(v, 'timeZone', '')
					};
				}),
				emailAddresses: _.map(prescriber.emailAddresses, (v) => {
					return {
						emailAddressId: v.id,
						emailAddressType: _.toNumber(v.type),
						address: _.get(v, 'address', ''),
						language: _.get(v, 'language', '')
					};
				}),
				prescriberLicenses: _.map(prescriber.prescriberLicenses, (v) => {
					return {
						prescriberLicenseId: v.prescriberLicenseId,
						licenseType: _.toNumber(v.licenseType),
						licenseNumber: _.get(v, 'licenseNumber', '')
					};
				})
			};

			// TODO: wrap in an edge call
			return this.$q.when(this.prescriberV3Api.prescriberUpsearchByTenantId({
				canInsert: true,
				canUpdate: true,
				skipSearch: false,
				model: request
			}).then((response) => {
				return new pme.Prescriber(response.data.data);
			}));
        }

		search(prescriber: pme.Prescriber): angular.IPromise<pme.Prescriber[]> {
			const request: DeepPartial<psgm.PrescriberSearchRequestModel> = {
				maxRecordCount: 20,
				firstName: _.get(prescriber, 'firstName', ''),
				lastName: _.get(prescriber, 'lastName', ''),
				dea: _.get(_.find(_.get(prescriber, 'externalIds', []), { type: pmg.ExternalIdType.DEANUMBER } as any), 'value', ''), // 5: DEANUMBER
				npi: _.get(_.find(_.get(prescriber, 'externalIds', []), { type: pmg.ExternalIdType.NPINUMBER } as any), 'value', ''), // 6: NPINUMBER
				stateLicenseId: _.get(_.find(_.get(prescriber, 'externalIds', []), { type: pmg.ExternalIdType.STATELICENSENUMBER } as any), 'value', ''), // 9: STATELICENSENUMBER
				prescriberTaxId: _.get(_.find(_.get(prescriber, 'externalIds', []), { type: pmg.ExternalIdType.MEDICALID } as any), 'value', ''), // 12: MEDICALID
				addressOne: _.get(prescriber, 'addresses[0].address1', ''),
				city: _.get(prescriber, 'addresses[0].city', ''),
				state: _.get(prescriber, 'addresses[0].state', ''),
				zip: _.get(prescriber, 'addresses[0].zip', ''),
				externalIdType: null,
				externalId: ''
			};

			// TODO: wrap in an edge call
			return this.$q.when(this.prescriberV3Api.prescriberSearchByTenantId(request).then((response) => {
				const temp: pme.Prescriber[] = [];

				for (const prescriber of response.data.data.prescribers) {
					temp.push(new pme.Prescriber(prescriber));
                }

				return temp;
            }));
        }
    }
}